import type { PaymentsV3StatusResponse, TOmanCreditCardForm, TOmanFormatData } from '../types/oman.types'

// ─── Error extraction ───────────────────────────────────────────────────────
// Walks the common Axios error shapes used by the Oman APIs and falls back to
// `Error.message` / a generic copy. Extracted so the hook stays focused on
// orchestration.
export const extractErrorMessage = (err: unknown): string => {
    const axiosErr = err as {
        response?: {
            data?: {
                error?: { error_message?: string; message?: string }
                error_message?: string
                message?: string
            }
        }
    }
    return (
        axiosErr?.response?.data?.error?.error_message ??
        axiosErr?.response?.data?.error?.message ??
        axiosErr?.response?.data?.error_message ??
        axiosErr?.response?.data?.message ??
        (err instanceof Error ? err.message : 'Payment failed. Please try again.')
    )
}

// ─── Terminal resolution ───────────────────────────────────────────────────
// Signals whether polling should stop:
//   'success' → order created
//   'error'   → payment declined / hydra failed / hydra timeout
//   null      → still processing, keep polling
export type TerminalKind = 'success' | 'error'

export const resolveTerminal = (data: PaymentsV3StatusResponse): TerminalKind | null => {
    if (data.hydra_status === 'success') return 'success'
    if (data.hydra_status === 'fail' || data.hydra_status === 'timeout') return 'error'
    if (data.payment_status === 'fail') return 'error'
    return null
}

// ─── Date/year helper ──────────────────────────────────────────────────────
// `cardForm.expYear` is stored as a 2-digit string ("28") because the MM/YY
// picker UI uses 2 digits. Hydra and the encryption pipeline both require
// 4-digit years ("2028-01") — pad here so callers don't have to remember.
export const toFourDigitYear = (yy: string): string => {
    const trimmed = (yy ?? '').trim()
    if (trimmed.length === 4) return trimmed
    return String(2000 + Number(trimmed || 0))
}

// ─── Name helpers ──────────────────────────────────────────────────────────
export const splitFullname = (full: string): { firstName: string; lastName: string } => {
    const [first = '', ...rest] = (full ?? '').trim().split(/\s+/)
    return { firstName: first, lastName: rest.join(' ') }
}

// ─── Billing info ──────────────────────────────────────────────────────────
type CustomerLike = {
    humanName?: {
        firstName?: string
        lastName?: string
        fullName?: string
        ['fullName@en']?: string
    }
}

export type BillingInfo = {
    billing: {
        country: string
        address1?: string
        address2?: string
        city?: string
        state?: string
    }
    email: string
    phone: string
    name: { firstName: string; lastName: string }
}

/**
 * Builds the bill-to payload for `saveCardAlias`.
 *
 * For "same as shipping", individual address fields come straight from the
 * raw format response (`ushop.detail_page`). The mapped `formatData.address`
 * is a pre-joined display string ("addr1, addr2, city, state") and would
 * corrupt `address1` on the wire.
 *
 * For the bill-to name, prefer the logged-in customer's `humanName` (already
 * split into first/last). Fall back to splitting the order fullname when
 * humanName is empty (legacy `splitName` behaviour).
 */
export const buildBillingInfo = ({
    cardForm,
    formatData,
    formatRaw,
    customer
}: {
    cardForm: TOmanCreditCardForm
    formatData?: TOmanFormatData | null
    formatRaw?: Record<string, unknown> | null
    customer: CustomerLike
}): BillingInfo => {
    const detailPage = ((formatRaw?.ushop as Record<string, unknown> | undefined)?.detail_page as Record<string, unknown> | undefined) ?? {}
    const detailStr = (k: string) => (typeof detailPage[k] === 'string' ? (detailPage[k] as string) : undefined)

    const billing = cardForm.billingSameAsShipping
        ? {
              country: detailStr('country') ?? formatData?.country ?? 'OM',
              address1: detailStr('address') ?? detailStr('address1'),
              address2: detailStr('address2'),
              city: detailStr('city'),
              state: detailStr('state')
          }
        : {
              country: cardForm.billing.country,
              address1: cardForm.billing.address1,
              address2: cardForm.billing.address2,
              city: cardForm.billing.city,
              state: cardForm.billing.state
          }

    const customerFirst = customer?.humanName?.firstName || ''
    const customerLast = customer?.humanName?.lastName || ''
    const fallbackFull = formatData?.fullname ?? [formatData?.firstname, formatData?.lastname].filter(Boolean).join(' ') ?? ''
    const fallback = splitFullname(fallbackFull)

    return {
        billing,
        email: formatData?.email ?? '',
        phone: formatData?.phone ?? '',
        name: cardForm.billingSameAsShipping
            ? {
                  firstName: customerFirst || fallback.firstName,
                  lastName: customerLast || fallback.lastName
              }
            : splitFullname(cardForm.billing.firstname || '')
    }
}

// ─── Payer name resolution ─────────────────────────────────────────────────
export const resolvePayerName = ({
    customer,
    formatData,
    fallback
}: {
    customer: CustomerLike
    formatData?: TOmanFormatData | null
    fallback: string
}): string =>
    customer.humanName?.['fullName@en'] ||
    customer.humanName?.fullName ||
    formatData?.fullname ||
    [formatData?.firstname, formatData?.lastname].filter(Boolean).join(' ') ||
    fallback
